"use client";

import React from "react";
import { Calculator, Stethoscope, Scale, Wrench, Building2, Laptop, ShoppingBag, Compass, ArrowRight, CircleCheck as CheckCircle2, UserCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const sectorsList = [{
  id: "accountants",
  title: "Accountants & Financial Services",
  iconName: "calculator",
  description: "Target high-value personal and business tax clients in your target region with trust-focused, intent-driven local and national SEO campaigns.",
  focus: "High-Intent Lead Gen & Local Maps"
}, {
  id: "solicitors",
  title: "Solicitors & Legal Practices",
  iconName: "scale",
  description: "Capture competitive legal searches for personal injury, family law, corporate, and conveyancing services with authoritative content and link strategies.",
  focus: "E-E-A-T & High-Value Inquiries"
}, {
  id: "healthcare",
  title: "Dentists & Private Healthcare",
  iconName: "healthcare",
  description: "Dominate Google Maps and local search for private dental treatments, cosmetic procedures, and specialized health clinic services.",
  focus: "Google Business & Patient Acquisition"
}, {
  id: "trades",
  title: "Plumbers, Electricians & Trades",
  iconName: "trades",
  description: "Generate reliable emergency calls and scheduled domestic/commercial quote requests directly through top local search visibility.",
  focus: "Local Map Pack & Mobile Search"
}, {
  id: "realestate",
  title: "Real Estate & Estate Agents",
  iconName: "realestate",
  description: "Outrank regional listing platforms for local property valuation and sales keywords, securing direct vendor and landlord leads.",
  focus: "Regional SEO & Structured Data"
}, {
  id: "b2b",
  title: "B2B & Software / SaaS",
  iconName: "b2b",
  description: "Shorten long sales cycles with technical B2B SEO targeting niche decision-maker queries, technical product features, and commercial intent.",
  focus: "Technical SEO & Commercial Keywords"
}, {
  id: "ecommerce",
  title: "E-commerce & Retail Brands",
  iconName: "ecommerce",
  description: "Optimize vast product catalogs, category hierarchies, and faceted navigation to drive sustainable organic revenue growth.",
  focus: "Faceted Technical SEO & Revenue"
}, {
  id: "construction",
  title: "Architects & Construction",
  iconName: "construction",
  description: "Position your practice for multi-million pound residential and commercial development projects across your regional coverage area.",
  focus: "Portfolio SEO & Regional Trust"
}];
export default function Sectors() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const renderIcon = (name: string) => {
    const iconClasses = "size-6 text-primary shrink-0";
    switch (name) {
      case "calculator":
        return <Calculator className={iconClasses} />;
      case "scale":
        return <Scale className={iconClasses} />;
      case "healthcare":
        return <Stethoscope className={iconClasses} />;
      case "trades":
        return <Wrench className={iconClasses} />;
      case "realestate":
        return <Building2 className={iconClasses} />;
      case "b2b":
        return <Laptop className={iconClasses} />;
      case "ecommerce":
        return <ShoppingBag className={iconClasses} />;
      case "construction":
        return <Compass className={iconClasses} />;
      default:
        return <CheckCircle2 className={iconClasses} />;
    }
  };
  return <section id="industrysectorsgrid" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 relative z-10 max-w-7xl">
        {}
        <div className="max-w-3xl mx-auto text-center mb-16 space-y-4">
          <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider">
            Sectors & Industries
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight font-default">
            Tailored SEO Strategies for Your Industry
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            Every sector has distinct search behaviors, compliance rules, and customer purchase paths. I design and execute bespoke SEO strategies tailored to your exact target audience.
          </p>
        </div>

        {}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {sectorsList.map((sector, i) => <motion.div key={sector.id} variants={cardVariants} data-index={i} className="h-full">
              <Card className="h-full bg-card border-border border hover:border-primary/40 transition-[border-color,box-shadow] duration-300 shadow-xs flex flex-col justify-between">
                <CardHeader className="space-y-4 pt-6">
                  <div className="p-3 bg-primary/10 rounded-lg w-fit flex items-center justify-center">
                    {renderIcon(sector.iconName)}
                  </div>
                  <CardTitle className="text-xl font-semibold font-default leading-snug">
                    {sector.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="space-y-4 flex-1">
                  <CardDescription className="text-muted-foreground text-sm leading-relaxed font-default">
                    {sector.description}
                  </CardDescription>
                </CardContent>

                <div className="px-6 pb-6 pt-2">
                  <div className="pt-3 border-t border-border flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span className="text-xs font-medium text-foreground/80 font-default">
                      {sector.focus}
                    </span>
                  </div>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>

        {}
        <div className="mt-16 bg-muted/50 border border-border rounded-2xl p-8 md:p-10 flex flex-col lg:flex-row items-center justify-between gap-8 shadow-xs">
          <div className="space-y-3 max-w-2xl text-center lg:text-left">
            <div className="inline-flex items-center gap-2 text-primary font-medium text-sm">
              <UserCheck className="size-5" />
              <span>Direct Consultant Access</span>
            </div>
            <h3 className="text-2xl font-semibold font-default">
              Don’t see your exact industry listed?
            </h3>
            <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
              I audit, strategize, and execute campaigns across specialized niche B2B and consumer verticals. When you partner with YE Consultancy, you work directly with me—never account managers or junior staff.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4 w-full lg:w-auto shrink-0">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/CaseStudiesPage">
                Explore Case Studies
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}